import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "../../lib/api";

/**
 * Render a tenant's legal document (Privacy / Terms / Shipping / Refund), or
 * its About Us / Contact Us page (`kind` prop, at /about and /contact —
 * iter122; 404 until the store publishes it).
 *
 * Stored body is plain text with a markdown-lite subset:
 *   `# Heading`     → h2
 *   `## Sub`        → h3
 *   `- item`        → ul/li (consecutive lines coalesced)
 *   blank line      → paragraph break
 *
 * The tiny renderer below is intentional — react-markdown adds ~80kb to
 * the bundle and we only need 4 directives. If we ever need links /
 * bold / images in legal copy, swap to react-markdown then.
 */

const KIND_TO_TITLE = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  shipping: "Shipping Policy",
  refund: "Refund & Returns Policy",
  about: "About Us",
  contact: "Contact Us",
};

function renderLegalBody(body) {
  if (!body) return null;
  const lines = body.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  let list = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: "p", text: para.join(" ") });
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push({ type: "ul", items: list });
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line === "") {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h3", text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith("# ")) {
      flushPara();
      flushList();
      blocks.push({ type: "h2", text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith("- ")) {
      flushPara();
      list.push(line.slice(2).trim());
      continue;
    }
    flushList();
    para.push(line);
  }
  flushPara();
  flushList();

  return blocks.map((b, i) => {
    if (b.type === "h2")
      return <h2 key={i} className="font-display text-3xl mt-8 mb-3">{b.text}</h2>;
    if (b.type === "h3")
      return <h3 key={i} className="font-display text-xl mt-6 mb-2">{b.text}</h3>;
    if (b.type === "ul")
      return (
        <ul key={i} className="list-disc pl-6 space-y-1.5 my-3 text-sm leading-relaxed">
          {b.items.map((it, j) => <li key={j}>{it}</li>)}
        </ul>
      );
    return <p key={i} className="my-3 text-sm leading-relaxed">{b.text}</p>;
  });
}

const OTHER_KINDS_ORDER = ["privacy", "terms", "shipping", "refund"];

export default function LegalPage({ kind: fixedKind }) {
  const params = useParams();
  const kind = fixedKind || params.kind;
  const { pathname } = useLocation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const validKind = useMemo(
    () => (kind && KIND_TO_TITLE[kind] ? kind : null),
    [kind],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) {
        setLoading(true);
        setError("");
        setDoc(null);
      }
      if (!validKind) {
        if (alive) {
          setLoading(false);
          setError("Unknown legal document.");
        }
        return;
      }
      try {
        const { data } = await api.get(`/legal/${validKind}`);
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load this document.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [validKind, pathname]);

  return (
    <div
      className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16"
      data-testid={`legal-page-${validKind || "unknown"}`}
    >
      {loading && (
        <div data-testid="legal-loading" className="text-sm text-[hsl(var(--kb-muted))]">
          Loading…
        </div>
      )}
      {!loading && error && (
        <div
          data-testid="legal-error"
          className="border border-red-200 bg-red-50 text-red-700 p-4 text-sm"
        >
          {error}
        </div>
      )}
      {!loading && doc && (
        <article data-testid="legal-content">
          <div className="overline text-[hsl(var(--kb-muted))] mb-2">
            {doc.tenant_name || ""}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mb-2" data-testid="legal-title">
            {doc.title || KIND_TO_TITLE[validKind]}
          </h1>
          {doc.updated_at && (
            <div
              className="text-xs text-[hsl(var(--kb-muted))] mb-8"
              data-testid="legal-updated-at"
            >
              Last updated {new Date(doc.updated_at).toLocaleDateString(undefined, {
                year: "numeric", month: "long", day: "numeric",
              })}
            </div>
          )}
          <div className="text-[hsl(var(--kb-text))]">
            {renderLegalBody(doc.body)}
          </div>
          {/* Cross-links to other legal pages */}
          <div
            className="mt-12 pt-6 border-t border-[hsl(var(--kb-border))] flex flex-wrap gap-x-6 gap-y-2 text-sm"
            data-testid="legal-cross-links"
          >
            {OTHER_KINDS_ORDER.filter((k) => k !== validKind).map((k) => (
              <Link
                key={k}
                to={`/legal/${k}`}
                className="text-[hsl(var(--kb-primary))] hover:underline"
                data-testid={`legal-other-${k}`}
              >
                {KIND_TO_TITLE[k]}
              </Link>
            ))}
          </div>
        </article>
      )}
    </div>
  );
}
